import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, X, Send, Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_CHARS = 320;

// Bilingual strings — wired for a future full EN/ES conversion.
// Toggle by passing lang="es" or by setting <html lang="es">.
const STRINGS = {
    en: {
        button: 'Text Us',
        title: 'Text Us',
        yourName: 'Your Name',
        namePh: 'First & Last Name',
        org: 'Organization',
        orgPh: 'Your nonprofit / org (optional)',
        yourPhone: 'Your Mobile Number',
        phonePh: '(555) 123-4567',
        messageOpt: 'Message',
        messagePh: 'How can we help?',
        charCount: '{count}/{max}',
        sendMessage: 'Send Message',
        messageSent: 'Message Sent!',
        wellGetBack: "We'll text you back shortly.",
        close: 'Close',
        openMessages: 'Open Messages App',
        fromCF: 'from CharityFundraiser',
    },
    es: {
        button: 'Envíanos un Mensaje',
        title: 'Envíanos un Mensaje',
        yourName: 'Tu Nombre',
        namePh: 'Nombre y Apellido',
        org: 'Organización',
        orgPh: 'Tu organización sin fines de lucro (opcional)',
        yourPhone: 'Tu Número de Celular',
        phonePh: '(555) 123-4567',
        messageOpt: 'Mensaje',
        messagePh: '¿En qué podemos ayudarte?',
        charCount: '{count}/{max}',
        sendMessage: 'Enviar Mensaje',
        messageSent: '¡Mensaje Enviado!',
        wellGetBack: 'Te responderemos pronto.',
        close: 'Cerrar',
        openMessages: 'Abrir App de Mensajes',
        fromCF: 'desde CharityFundraiser',
    },
};

const detectLang = () => {
    if (typeof document !== 'undefined') {
        const htmlLang = (document.documentElement.lang || '').toLowerCase();
        if (htmlLang.startsWith('es')) return 'es';
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/es')) return 'es';
    }
    return 'en';
};

export default function TextUsWidget({ source = 'CF' }) {
    const lang = detectLang();
    const s = STRINGS[lang];

    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [org, setOrg] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [mainNumber, setMainNumber] = useState('');
    const panelRef = useRef(null);

    useEffect(() => {
        setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent || ''));
        axios.get(`${API}/api/sms/text-us-config`)
            .then(({ data }) => {
                const num = (data?.number || '').replace(/\D/g, '').replace(/^1/, '');
                if (num) setMainNumber(num);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const totalChars = message.length;
    const displayNumber = mainNumber
        ? `(${mainNumber.slice(0, 3)}) ${mainNumber.slice(3, 6)}-${mainNumber.slice(6)}`
        : '';

    const composedSmsBody = () => {
        const parts = [];
        if (name.trim()) parts.push(name.trim());
        if (org.trim()) parts.push(`(${org.trim()})`);
        if (message.trim()) parts.push(message.trim());
        const body = parts.join(' | ') || s.fromCF;
        return `[CF] ${body}`;
    };

    const handleMobileSend = () => {
        if (!mainNumber) return;
        const body = encodeURIComponent(composedSmsBody());
        window.location.href = `sms:+1${mainNumber}?body=${body}`;
    };

    const handleDesktopSend = async () => {
        setError('');
        if (!phone.trim() || phone.replace(/\D/g, '').length < 7) {
            setError('Please enter a valid phone number.');
            return;
        }
        if (!name.trim() && !message.trim()) {
            setError('Please add your name or a short message.');
            return;
        }
        setSending(true);
        try {
            const cleanPhone = phone.replace(/\D/g, '');
            const formattedPhone = cleanPhone.startsWith('1') ? `+${cleanPhone}` : `+1${cleanPhone}`;
            await axios.post(`${API}/api/sms/text-us`, {
                visitor_phone: formattedPhone,
                name: name.trim(),
                organization: org.trim(),
                message: message.trim(),
                source,
                preferred_language: lang,
            });
            setSent(true);
        } catch (e) {
            console.error(e);
            setError('Could not send. Please try again or call us directly.');
        }
        setSending(false);
    };

    const reset = () => {
        setName(''); setOrg(''); setPhone(''); setMessage('');
        setSent(false); setError(''); setOpen(false);
    };

    return (
        <>
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="text-us-btn"
                    aria-label={s.button}
                    className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] flex flex-col items-center gap-1 bg-emerald-700 hover:bg-emerald-600 text-white px-1.5 py-2.5 sm:pl-2 sm:pr-3 sm:py-4 rounded-l-xl shadow-lg transition-all hover:pr-4"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                    <MessageSquare className="w-4 h-4 rotate-90" />
                    <span className="text-sm font-medium tracking-wide hidden sm:inline">{s.button}</span>
                </button>
            )}

            {open && (
                <div
                    ref={panelRef}
                    data-testid="text-us-panel"
                    className="fixed right-4 bottom-4 sm:right-6 sm:bottom-6 z-[60] w-[92vw] max-w-sm bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
                >
                    <div className="flex items-center justify-between bg-emerald-700 text-white px-4 py-3">
                        <div>
                            <div className="text-sm font-semibold">{s.title}</div>
                            {displayNumber && <div className="text-xs opacity-90">{displayNumber}</div>}
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="text-us-close-btn"
                            className="text-white/80 hover:text-white"
                            aria-label={s.close}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {sent ? (
                        <div className="p-6 text-center" data-testid="text-us-success">
                            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                            <div className="font-semibold text-gray-800">{s.messageSent}</div>
                            <div className="text-sm text-gray-600 mt-1">{s.wellGetBack}</div>
                            <button
                                type="button"
                                onClick={reset}
                                data-testid="text-us-reset-btn"
                                className="mt-4 text-sm bg-emerald-700 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg"
                            >
                                {s.close}
                            </button>
                        </div>
                    ) : (
                        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{s.yourName}</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={s.namePh}
                                    data-testid="text-us-name-input"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{s.org}</label>
                                <input
                                    type="text"
                                    value={org}
                                    onChange={(e) => setOrg(e.target.value)}
                                    placeholder={s.orgPh}
                                    data-testid="text-us-org-input"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                                />
                            </div>
                            {!isMobile && (
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">{s.yourPhone}</label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder={s.phonePh}
                                        data-testid="text-us-phone-input"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{s.messageOpt}</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => { if (e.target.value.length <= MAX_CHARS) setMessage(e.target.value); }}
                                    placeholder={s.messagePh}
                                    rows={3}
                                    data-testid="text-us-message-input"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 resize-none"
                                />
                                <div className="text-[10px] text-gray-400 text-right mt-0.5">
                                    {s.charCount.replace('{count}', totalChars).replace('{max}', MAX_CHARS)}
                                </div>
                            </div>

                            {error && (
                                <div className="text-xs text-red-600" data-testid="text-us-error">{error}</div>
                            )}

                            {isMobile && mainNumber ? (
                                <button
                                    type="button"
                                    onClick={handleMobileSend}
                                    data-testid="text-us-mobile-send-btn"
                                    className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-600 text-white font-medium px-4 py-2.5 rounded-lg text-sm"
                                >
                                    <Send className="w-4 h-4" />
                                    {s.openMessages}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleDesktopSend}
                                    disabled={sending}
                                    data-testid="text-us-send-btn"
                                    className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-60 text-white font-medium px-4 py-2.5 rounded-lg text-sm"
                                >
                                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    {s.sendMessage}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
